import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from '~/lib/ui/dialog'
import { Button } from '~/lib/ui/button'
import { Textarea } from '~/lib/ui/textarea'
import { Label } from '~/lib/ui/label'
import { apiPost } from '~/util/api'
import { useToast } from '~/lib/ui/toast'
import { Input } from '~/lib/ui/input'
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '~/lib/ui/select'
import { cn } from '~/lib/utils'
import NewPostLandingEvent from './NewPostLandingEvent'
import { useFormatTime } from '~/hooks/useFormatTime'

const getDialogTitle = (event) => {
  switch (event) {
    case 'feedback':
      return 'Did you get any feedback related to the service?'
    case 'hired':
      return 'Report a Successful Transition!'
    case 'interview':
      return 'Got an Interview'
    case 'offer':
      return 'Got an Offer'
    default:
      return 'New Special Event'
  }
}

const getDialogDescription = (eventType) => {
  if (eventType === 'feedback')
    return 'Share any feedback you received related to the service.'
  if (eventType === 'hired')
    return 'Report details about a successful transition.'
  return 'Fill out the form to report a special event.'
}

const InputField = ({
  id,
  name,
  label,
  value,
  onChange,
  type = 'text',
  className,
}) => (
  <div className="mb-4 w-full px-2">
    <Label htmlFor={id} className="text-lg !font-medium">
      {label}
    </Label>
    <Input
      id={id}
      name={name}
      className={cn('mt-2', className)}
      value={value || ''}
      onChange={onChange}
      type={type}
    />
  </div>
)

const SelectField = ({ id, name, label, value, onChange, options }) => (
  <div className="mb-4 w-full px-2">
    <Label htmlFor={id} className="text-lg !font-medium">
      {label}
    </Label>
    <Select
      value={value || options[0].value}
      onValueChange={(val) => onChange({ target: { name, value: val } })}
    >
      <SelectTrigger id={id} name={name} className="mt-2">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
)

const NewSpecialEvent = ({ event, candidateId, onSuccess, children }) => {
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({
    event_type: event,
    candidateId: candidateId,
    notes: '',
  })
  const { toast } = useToast()
  const [isSaving, setIsSaving] = useState(false)

  const isHired = form.event_type === 'hired'
  const isFeedback = form.event_type === 'feedback'

  const formatTime = useFormatTime()

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSaving(true)
    let submitData
    if (isFeedback) {
      submitData = {
        event_type: form.event_type,
        candidate_id: form.candidateId,
        notes: form.notes,
      }
    } else {
      let formattedForm = { ...form }
      if (form.date_hired) {
        formattedForm.date_hired = formatTime(form.date_hired, 'short')
      }
      submitData = { ...formattedForm, candidate_id: form.candidateId }
      delete submitData.candidateId
    }

    try {
      await apiPost(`/coach/candidates/${form.candidateId}/special_events`, {
        feedback_special_event: submitData,
      })
      setIsSaving(false)
      setOpen(false)
      toast({
        title: 'Special Event Created',
        description: 'The special event was successfully created.',
        status: 'success',
      })
      if (onSuccess) onSuccess()
    } catch (error) {
      setIsSaving(false)
      const description =
        error?.response?.data?.error ||
        'An error occurred while creating the special event.'
      toast({
        description,
        title: 'Could Not Create Special Event',
        variant: 'destructive',
        duration: 10_000,
      })
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={() => setOpen(!open)}
      className="rounded-2xl"
    >
      <DialogTrigger asChild className="cursor-pointer">
        <Button>{children}</Button>
      </DialogTrigger>
      <DialogContent
        className="font-roboto lg:max-w-3xl xl:max-w-5xl"
        variant="default"
        dialogCloseClass="text-black"
      >
        <DialogHeader>
          <DialogTitle className="w-full text-center" asChild>
            <h2 className="w-full text-2xl">{getDialogTitle(event)}</h2>
          </DialogTitle>
        </DialogHeader>
        {event !== 'post_landing_support' && (
          <DialogDescription>
            {getDialogDescription(form.event_type)}
          </DialogDescription>
        )}
        <div className="max-h-[80vh] overflow-y-auto px-4 pb-4">
          {event === 'post_landing_support' ? (
            <NewPostLandingEvent
              candidateId={candidateId}
              onSuccess={() => setOpen(false)}
              isSaving={isSaving}
              setIsSaving={setIsSaving}
              toast={toast}
            />
          ) : (
            <form onSubmit={handleSubmit}>
              <input type="hidden" name="event_type" value={form.event_type} />
              <input
                type="hidden"
                name="candidateId"
                value={form.candidateId}
              />

              {!isFeedback && (
                <>
                  <div className="-mx-2 flex flex-wrap">
                    <div className="w-full md:w-1/2">
                      <InputField
                        id="hired_by"
                        name="hired_by"
                        label={
                          isHired
                            ? 'New company or organization name'
                            : 'Company name'
                        }
                        value={form.hired_by}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="w-full md:w-1/2">
                      <InputField
                        id="location"
                        name="location"
                        label="Location"
                        value={form.location}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  {isHired ? (
                    <div className="-mx-2 flex flex-wrap">
                      <div className="w-full md:w-1/3">
                        <InputField
                          id="role"
                          name="role"
                          label="New Job Title or Role"
                          value={form.role}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="w-full md:w-1/3">
                        <SelectField
                          id="salary"
                          name="salary"
                          label="Salary Change (More or Less)"
                          value={form.salary}
                          onChange={handleChange}
                          options={[
                            { value: 'Unknown', label: 'Unknown' },
                            {
                              value: 'Higher hourly or annually',
                              label: 'Higher hourly or annually',
                            },
                            {
                              value: 'Lower than previous',
                              label: 'Lower than previous',
                            },
                            {
                              value: 'Did not disclose',
                              label: 'Did not disclose',
                            },
                          ]}
                        />
                      </div>
                      <div className="w-full md:w-1/3">
                        <InputField
                          id="date_hired"
                          name="date_hired"
                          label="Date accepted"
                          value={form.date_hired}
                          onChange={handleChange}
                          type="date"
                          className="max-w-40"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="-mx-2 flex flex-wrap">
                      <div className="w-full md:w-1/2">
                        <InputField
                          id="role"
                          name="role"
                          label="Role"
                          value={form.role}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="w-full md:w-1/2">
                        <InputField
                          id="salary"
                          name="salary"
                          label="Salary"
                          value={form.salary}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="mb-4">
                <Label htmlFor="notes" className="text-lg !font-medium">
                  Notes
                </Label>
                <Textarea
                  id="notes"
                  name="notes"
                  className="mt-2"
                  value={form.notes}
                  onChange={handleChange}
                />
              </div>

              <div className="flex justify-end">
                <Button type="submit" disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default NewSpecialEvent
